/**
 * Unified Auth Service
 * Handles all authentication logic for both cloud and self-hosted environments
 */

import { getAuthConfig } from '../../utils/authConfig';
import { createFileLogger } from '../../utils/logger';
import { clearAllAuthState } from '../../utils/logoutUtils';
import { parseJwtPayload } from '../../utils/jwtUtils';

const log = createFileLogger('AUTH_SERVICE');

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  picture?: string;
  has_founder_access?: boolean;
  access_level?: string;
}

export interface AuthTokens {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

export interface SignupRequest {
  email: string;
  password: string;
  persona?: string;
  userMetadata?: Record<string, any>;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  user?: AuthUser;
  tokens?: AuthTokens;
  space_id?: string;
  welcome_doc_id?: string;
  access_token?: string; // For login responses
  message?: string;
  error?: string;
}

export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

class AuthService {
  private config = getAuthConfig();

  /**
   * Validate email format
   */
  validateEmail(email: string): ValidationResult {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    
    return {
      isValid,
      message: isValid ? undefined : 'Please enter a valid email address'
    };
  }

  /**
   * Validate password strength
   */
  validatePassword(password: string): ValidationResult {
    const checks = {
      length: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };

    const isValid = checks.length && 
                   checks.hasUppercase && 
                   checks.hasLowercase && 
                   (checks.hasNumber || checks.hasSpecialChar);

    return {
      isValid,
      message: isValid ? undefined : 'Password must be at least 8 characters with uppercase, lowercase, and a number or symbol'
    };
  }

  /**
   * Sign up a new user
   */
  async signup(request: SignupRequest): Promise<AuthResponse> {
    log.log('Starting signup process', { email: request.email, environment: this.config.environment });

    // Validate inputs
    const emailValidation = this.validateEmail(request.email);
    if (!emailValidation.isValid) {
      return { success: false, error: emailValidation.message };
    }

    const passwordValidation = this.validatePassword(request.password);
    if (!passwordValidation.isValid) {
      return { success: false, error: passwordValidation.message };
    }

    try {
      const response = await fetch(`${this.config.apiUrl}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: this.config.useHttpOnlyCookies ? 'include' : 'omit',
        body: JSON.stringify({
          email: request.email,
          password: request.password,
          persona: request.persona,
          user_metadata: request.userMetadata
        })
      });

      const data = await response.json();

      log.log('Signup API response', {
        status: response.status,
        success: data.success,
        hasUser: !!data.user,
        hasTokens: !!data.tokens,
        environment: this.config.environment
      });

      if (response.ok && data.success) {
        // Handle different response formats (nested data vs top-level)
        const responseData = data.data || data;
        
        // Store authentication token
        await this.storeAuthToken(responseData.tokens || { access_token: data.access_token });

        log.log('Signup successful', {
          userId: responseData.user?.id,
          spaceId: responseData.space_id,
          environment: this.config.environment
        });

        return {
          success: true,
          user: responseData.user,
          tokens: responseData.tokens,
          space_id: responseData.space_id,
          welcome_doc_id: responseData.welcome_doc_id
        };
      }

      // Handle specific error cases
      if (response.status === 409) {
        return {
          success: false,
          error: 'An account with this email already exists. Please try signing in instead.'
        };
      }

      return {
        success: false,
        error: data.message || 'Signup failed. Please try again.'
      };

    } catch (error) {
      log.error('Signup request failed', error);
      return {
        success: false,
        error: 'Failed to create account. Please check your connection and try again.'
      };
    }
  }

  /**
   * Sign in an existing user
   */
  async login(request: LoginRequest): Promise<AuthResponse> {
    log.log('Starting login process', { email: request.email, environment: this.config.environment });

    // Validate inputs
    const emailValidation = this.validateEmail(request.email);
    if (!emailValidation.isValid) {
      return { success: false, error: emailValidation.message };
    }

    if (!request.password.trim()) {
      return { success: false, error: 'Password is required' };
    }

    try {
      const response = await fetch(`${this.config.apiUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: this.config.useHttpOnlyCookies ? 'include' : 'omit',
        body: JSON.stringify({
          email: request.email,
          password: request.password
        })
      });

      const data = await response.json();

      log.log('Login API response', {
        status: response.status,
        success: data.success,
        hasUser: !!data.user,
        hasAccessToken: !!data.access_token,
        environment: this.config.environment
      });

      if (response.ok && data.success) {
        // Store authentication token
        const tokens = data.tokens || { access_token: data.access_token };
        await this.storeAuthToken(tokens);

        log.log('Login successful', {
          userId: data.user?.id,
          spaceId: data.space_id,
          environment: this.config.environment
        });

        return {
          success: true,
          user: data.user,
          tokens: tokens,
          space_id: data.space_id,
          welcome_doc_id: data.welcome_doc_id,
          access_token: data.access_token
        };
      }

      return {
        success: false,
        error: data.message || 'Login failed. Please check your credentials.'
      };

    } catch (error) {
      log.error('Login request failed', error);
      return {
        success: false,
        error: 'Login failed. Please check your connection and try again.'
      };
    }
  }

  /**
   * Sign out the current user
   */
  async logout(): Promise<void> {
    log.log('Starting logout process', { environment: this.config.environment });

    try {
      // Call logout endpoint if using httpOnly cookies
      if (this.config.useHttpOnlyCookies) {
        await fetch(`${this.config.apiUrl}/auth/logout`, {
          method: 'POST',
          credentials: 'include'
        });
      }
    } catch (error) {
      log.warn('Logout request failed', error);
    }

    // Use centralized cleanup
    clearAllAuthState();
    
    log.log('Logout completed');
  }

  /**
   * Get current authentication token
   */
  getAccessToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    try {
      // Check if token is expired
      
      const payload = parseJwtPayload(token);
      return payload && payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  /**
   * Get current user info from stored token
   */
  getCurrentUser(): AuthUser | null {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      
      const payload = parseJwtPayload(token);
      if (!payload) return null;
      
      return {
        id: payload.sub || payload.user_id,
        email: payload.email,
        name: payload.name,
        picture: payload.picture
      };
    } catch {
      return null;
    }
  }

  /**
   * Refresh authentication token
   * Works for both self-hosted (httpOnly cookies) and cloud (JWT refresh tokens)
   */
  async refreshToken(): Promise<boolean> {
    try {
      // For self-hosted with httpOnly cookies
      if (this.config.useHttpOnlyCookies) {
        const response = await fetch(`${this.config.apiUrl}/auth/refresh`, {
          method: 'POST',
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data.access_token) {
            localStorage.setItem('authToken', data.access_token);
            if (data.id_token) {
              localStorage.setItem('id_token', data.id_token);
            }
            return true;
          }
        }
        return false;
      }

      // For cloud with JWT refresh tokens
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        log.warn('No refresh token available for cloud refresh');
        return false;
      }

      const response = await fetch(`${this.config.apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.access_token) {
          localStorage.setItem('authToken', data.access_token);
          if (data.id_token) {
            localStorage.setItem('id_token', data.id_token);
          }
          if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
          }
          log.log('Token refresh successful');
          return true;
        }
      }

      log.warn('Token refresh failed:', response.status);
      return false;
    } catch (error) {
      log.error('Token refresh failed', error);
      return false;
    }
  }

  /**
   * Reset password
   */
  async resetPassword(email: string): Promise<{ success: boolean; message: string }> {
    const emailValidation = this.validateEmail(email);
    if (!emailValidation.isValid) {
      return { success: false, message: emailValidation.message! };
    }

    try {
      const response = await fetch(`${this.config.apiUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      // Always return success message for security (don't leak user existence)
      return {
        success: true,
        message: data.message || 'If that email exists, a reset link was sent'
      };

    } catch (error) {
      log.error('Password reset request failed', error);
      return {
        success: false,
        message: 'Password reset failed. Please try again.'
      };
    }
  }

  /**
   * Get auth configuration
   */
  getConfig() {
    return this.config;
  }

  /**
   * Store authentication token based on environment
   */
  private async storeAuthToken(tokens: AuthTokens | { access_token: string }): Promise<void> {
    if (this.config.useHttpOnlyCookies) {
      // Self-hosted: token is automatically stored in httpOnly cookie by the server
      log.log('Token stored in httpOnly cookie by server');
    } else {
      // Cloud: store in localStorage
      if ('access_token' in tokens && tokens.access_token) {
        localStorage.setItem('authToken', tokens.access_token);
        log.log('Token stored in localStorage');
      }
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
